import { Tabs } from 'radix-ui';
import React, { useEffect, useState } from 'react';

import {
    Footer,
    Header
} from '$partials/global';

import {
    IconAdd,
    IconMinus,
    IconSearch,
    IconSort
} from '$partials/icons';

import { SearchCarouselBlock } from '$partials/modules/blocks';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */
/**
 * @type {import('$types/components/pages.d.ts').GbbpbPage}
 */
const GbbpbPage = (props) => {
    const page = props.data.page;

    // state
    const [listing, setListing] = useState(page?.items?.edges || []);
    const [sort, setSort] = useState('valuation');
    const [term, setTerm] = useState('');

    // hooks
    useEffect(() => {
        const originalListing = page?.items?.edges;
        let filteredListing = [];
        let sortedListing = [];

        // filter by search term
        if (term) {
            originalListing.forEach((itm) => {
                const names = itm?.node?.people?.map(i => i?.name?.toLowerCase())?.join(', ') || '';

                if (itm?.node?.title?.toLowerCase()?.includes(term)) {
                    filteredListing.push(itm);
                } else if (itm?.node?.founded?.toLowerCase()?.includes(term)) {
                    filteredListing.push(itm);
                } else if (itm?.node?.headquarters?.toLowerCase()?.includes(term)) {
                    filteredListing.push(itm);
                } else if (itm?.node?.industry?.toLowerCase()?.includes(term)) {
                    filteredListing.push(itm);
                } else if (names.includes(term)) {
                    filteredListing.push(itm);
                }
            });
        } else {
            filteredListing = originalListing;
        }

        // sort by chosen property
        if (sort) {
            let sortProperty = '';
            switch (sort) {
                case 'company':
                    sortProperty = 'title';
                    break;
                case 'founded':
                    sortProperty = 'founded';
                    break;
                case 'founder':
                    sortProperty = 'people';
                    break;
                case 'industry':
                    sortProperty = 'industry';
                    break;
                case 'valuation':
                    sortProperty = 'value';
                    break;
            }

            if (sortProperty === 'value') {
                sortedListing = filteredListing.toSorted((a, b) => {
                    const itemA = a?.node?.[sortProperty];
                    const itemB = b?.node?.[sortProperty];

                    if (itemA < itemB) { return 1; }
                    if (itemA > itemB) { return -1; }
                    return 0;
                });
            } else if (sortProperty === 'people') {
                sortedListing = filteredListing.toSorted((a, b) => {
                    const itemA = a?.node?.[sortProperty]?.map(p => p.name.toLowerCase()).join(',');
                    const itemB = b?.node?.[sortProperty].map(p => p.name.toLowerCase()).join(',');

                    if (itemA < itemB) { return -1; }
                    if (itemA > itemB) { return 1; }
                    return 0;
                });
            } else {
                sortedListing = filteredListing.toSorted((a, b) => {
                    const itemA = a?.node?.[sortProperty]?.toLowerCase();
                    const itemB = b?.node?.[sortProperty]?.toLowerCase();

                    if (itemA < itemB) { return -1; }
                    if (itemA > itemB) { return 1; }
                    return 0;
                });
            }
        } else {
            sortedListing = filteredListing;
        }

        setListing(sortedListing);
    }, [
        sort,
        term
    ]);

    // misc
    const listFormatter = new Intl.ListFormat(
        'en',
        {
            type: 'conjunction',
            style: 'long'
        }
    );

    // render
    return (
        <>
            <Header {...props?.data?.header} />

            <main id='main-content'>
                <div className='layout-container layout-site-outer-padding pb-8 md:pb-16'>
                    {/* Header */}
                    <div className='col-wrapper-breakout flex flex-col sm:flex-row sm:flex-wrap gap-48 sm:gap-0 mb-8 md:mb-16 pb-8 pt-24 rounded-lg bg-db-blue text-neutral-00'>
                        <div className='w-full sm:w-8/12 md:w-7/12 col-padding'>
                            <h1 className='type-style-sans-display'>
                                {page.title}
                            </h1>

                            {page?.sponsorMedia?.image?.src && (
                                <div
                                    aria-hidden='true'
                                    className='w-full sm:w-4/8 md:w-3/7 mt-40 pointer-events-none select-none'>
                                    <p className='type-style-single-line-eyebrow mb-16'>Sponsored By</p>

                                    <img
                                        alt={page.sponsorMedia.image.alt || 'Sponsor logo'}
                                        className='w-full'
                                        src={page.sponsorMedia.image.src} />
                                </div>
                            )}
                        </div>

                        <div className='w-full sm:w-4/12 md:w-5/12 col-padding pointer-events-none select-none'>
                            {page?.headerMedia?.image?.px2 && (
                                <img
                                    alt={page.headerMedia.image.alt || 'Header media image'}
                                    className='h-full w-full object-cover object-center'
                                    src={page.headerMedia.image.px2} />
                            )}
                        </div>
                    </div>

                    {/* Introduction */}
                    <div className='col-wrapper-breakout mb-8 md:mb-16 pb-16 pt-24 rounded-lg bg-surface-panel-blue'>
                        <div className='w-full col-padding'>
                            <Tabs.Root defaultValue='tab1'>
                                <div className='w-full overflow-x-auto pb-16 mb-16'>
                                    <Tabs.List
                                        aria-label='Introduction'
                                        className='p-2 border border-border-default rounded-[52px] whitespace-nowrap w-fit'>
                                        <Tabs.Trigger
                                            className='button-transparent data-[state=active]:button-primary'
                                            value='tab1'>
                                            Foreword
                                        </Tabs.Trigger>

                                        <Tabs.Trigger
                                            className='button-transparent data-[state=active]:button-primary'
                                            value='tab2'>
                                            Introduction
                                        </Tabs.Trigger>

                                        <Tabs.Trigger
                                            className='button-transparent data-[state=active]:button-primary'
                                            value='tab3'>
                                            Methodology
                                        </Tabs.Trigger>

                                        <Tabs.Trigger
                                            className='button-transparent data-[state=active]:button-primary'
                                            value='tab4'>
                                            Related articles
                                        </Tabs.Trigger>
                                    </Tabs.List>
                                </div>

                                <div>
                                    <Tabs.Content
                                        className='w-full sm:w-8/12 md:w-6/12 flex flex-col gap-16'
                                        value='tab1'>
                                        <h2 className='type-style-sans-h3-med'>
                                            Foreword
                                        </h2>

                                        {(page?.forewordAuthorImage?.image?.src || page?.forewordAuthorName || page?.forewordAuthorRole) && (
                                            <figure className='flex items-center gap-16'>
                                                {page?.forewordAuthorImage?.image?.src && (
                                                    <div className='h-48 w-48 rounded-full overflow-hidden pointer-events-none select-none'>
                                                        <img
                                                            alt={`Portrait of ${page.forewordAuthorName}`}
                                                            className='h-full w-full'
                                                            src={page.forewordAuthorImage.image.src} />
                                                    </div>
                                                )}

                                                <figcaption className=''>
                                                    <p className='type-style-sans-body-s font-medium'>
                                                        {page?.forewordAuthorName}
                                                    </p>

                                                    <p className='type-style-sans-body-s'>
                                                        {page?.forewordAuthorRole}
                                                    </p>
                                                </figcaption>
                                            </figure>
                                        )}

                                        <div
                                            className='rich-text rich-text--compact'
                                            dangerouslySetInnerHTML={{ __html: page.foreword }}/>
                                    </Tabs.Content>

                                    <Tabs.Content
                                        className='w-full sm:w-8/12 md:w-6/12 flex flex-col gap-16'
                                        value='tab2'>
                                        <h2 className='type-style-sans-h3-med'>
                                            Introduction
                                        </h2>

                                        <div
                                            className='rich-text rich-text--compact'
                                            dangerouslySetInnerHTML={{ __html: page.introduction }}/>
                                    </Tabs.Content>

                                    <Tabs.Content
                                        className='w-full sm:w-8/12 md:w-6/12 flex flex-col gap-16'
                                        value='tab3'>
                                        <h2 className='type-style-sans-h3-med'>
                                            Methodology
                                        </h2>

                                        <div
                                            className='rich-text rich-text--compact'
                                            dangerouslySetInnerHTML={{ __html: page.methodology }}/>
                                    </Tabs.Content>

                                    <Tabs.Content
                                        className='-mx-4 md:-mx-8 flex flex-col gap-16'
                                        value='tab4'>
                                        <h2 className='sr-only type-style-sans-h3-med'>
                                            Related articles
                                        </h2>

                                        <div className='-mt-24'>
                                            <SearchCarouselBlock
                                                desktopSlidesPerView={3.25}
                                                items={page?.content?.map(c => ({ node: c?.page }))} />
                                        </div>
                                    </Tabs.Content>
                                </div>
                            </Tabs.Root>
                        </div>
                    </div>

                    {/* Filters */}
                    <div className='col-wrapper-breakout px-0 flex flex-wrap sm:justify-end gap-8 mt-40 rounded-lg bg-surface-panel-white'>
                        <div className='w-full sm:w-fit flex'>
                            <div className='relative w-full sm:w-fit sm:ml-auto'>
                                <select
                                    className='input input--select input--square w-full'
                                    name='sort'
                                    onChange={(e) => setSort(e.target.value)}
                                    value={sort}>
                                    <option value='valuation'>By Valuation</option>
                                    <option value='company'>By Company Name</option>
                                    <option value='founder'>By Founder Name</option>
                                    <option value='industry'>By Industry</option>
                                    <option value='founded'>By Founded Year</option>
                                </select>

                                <div className='absolute left-20 top-12'>
                                    <IconSort />
                                </div>
                            </div>
                        </div>

                        <div className='w-full sm:w-fit flex'>
                            <div className='relative w-full sm:w-fit sm:ml-auto'>
                                <input
                                    className='input input--search input--square w-full'
                                    maxLength={20}
                                    name='term'
                                    onChange={(e) => setTerm(e.target.value.toLowerCase())}
                                    placeholder='Search'
                                    type='search' />

                                <div className='absolute left-20 top-12'>
                                    <IconSearch />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Listing */}
                    <section className='mt-16'>
                        <ol className='flex flex-col gap-8 sm:gap-16'>
                            {listing?.length > 0 && listing.map((itm, idx) => {
                                return (
                                    <li
                                        className='col-wrapper-breakout rounded-lg bg-surface-panel-blue'
                                        key={idx}>
                                        <details className='group'>
                                            <summary className='relative flex flex-wrap gap-16 sm:gap-0 items-start pb-24 pt-8 md:pt-16 cursor-pointer'>
                                                <span className='block w-full sm:w-1/12 md:w-2/12 col-padding'>
                                                    <span className='type-style-serif-h2'>
                                                        {itm?.node?.displayRank}
                                                    </span>
                                                </span>

                                                <span className='sm:relative w-full sm:w-11/12 md:w-10/12 col-padding flex flex-col gap-8'>
                                                    <span className='type-style-serif-h2'>{listFormatter.format(itm.node.people.map((p) => p?.name))}</span>

                                                    <span className='contents sm:flex gap-40'>
                                                        <span className='type-style-sans-h3'>{itm?.node?.title}</span>
                                                        <span className='type-style-sans-h3'>£{itm?.node?.value} billion</span>
                                                    </span>

                                                    <span className='absolute top-8 sm:top-0 right-8 md:right-16 block group-open:hidden'>
                                                        <span className='button-icon-transparent group-hover:button-icon-primary'>
                                                            <IconAdd />
                                                        </span>
                                                    </span>

                                                    <span className='absolute top-8 sm:top-0 right-8 md:right-16 hidden group-open:block'>
                                                        <span className='button-icon-transparent group-hover:button-icon-primary'>
                                                            <IconMinus />
                                                        </span>
                                                    </span>
                                                </span>
                                            </summary>

                                            <div className='flex flex-wrap border-t border-border-default'>
                                                <div className='w-full md:w-2/12' />

                                                {/* Short Bio */}
                                                <div className='w-full sm:w-9/12 md:w-6/12 col-padding py-24'>
                                                    {itm?.node?.shortBio && (
                                                        <div
                                                            className='rich-text type-style-sans-body'
                                                            dangerouslySetInnerHTML={{ __html: itm.node.shortBio }} />
                                                    )}

                                                    <p className='hidden sm:block mt-24'>
                                                        <a
                                                            className='button-outcome'
                                                            href={itm.node.url}>
                                                            View full profile
                                                        </a>
                                                    </p>
                                                </div>

                                                {/* Details */}
                                                <div className='w-full sm:w-3/12 md:w-4/12 sm:border-l border-border-default'>
                                                    <dl className='-ml-1 flex flex-wrap'>
                                                        {/* Industry */}
                                                        <div className='w-full md:w-2/4 p-8 md:p-16 border-b border-border-default'>
                                                            <dt className='type-style-single-line-eyebrow mb-8'>Industry</dt>
                                                            <dd>
                                                                {itm.node.industry}
                                                            </dd>
                                                        </div>


                                                        {/* Founded */}
                                                        <div className='w-full md:w-2/4 p-8 md:p-16 border-b border-border-default'>
                                                            <dt className='type-style-single-line-eyebrow mb-8'>Founded</dt>
                                                            <dd>
                                                                {itm.node.founded}
                                                            </dd>
                                                        </div>


                                                        {/* Headquarters */}
                                                        <div className='w-full md:w-2/4 p-8 md:p-16'>
                                                            <dt className='type-style-single-line-eyebrow mb-8'>HQ</dt>
                                                            <dd>
                                                                {itm.node.headquarters}
                                                            </dd>
                                                        </div>
                                                    </dl>
                                                </div>

                                                <p className='sm:hidden mb-24 mt-40 col-padding'>
                                                    <a
                                                        className='button-outcome'
                                                        href={itm.node.url}>
                                                        View full profile
                                                    </a>
                                                </p>
                                            </div>
                                        </details>
                                    </li>
                                );
                            })}
                        </ol>
                    </section>
                </div>
            </main>

            <Footer {...props?.data?.footer} />
        </>
    );
};

/* ========================================================================= */
/* Exports */
/* ========================================================================= */

export default GbbpbPage;
